// ADC-IMPLEMENTS: <adc-006a-feature-dashboard-01>
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldAlert, ShieldCheck, ArrowLeft, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SidebarProvider } from '@/components/ui/sidebar';
import Navbar from '@/components/Navbar';
import AppSidebar from '@/components/AppSidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { IncidentCard } from '@/components/IncidentCard';
import { IncidentDetailPanel } from '@/components/IncidentDetailPanel';
import { useIncidentContext } from '@/context/IncidentContext';
import { useAllIncidents } from '@/hooks/useAllIncidents';
import { useConnections } from '@/hooks/useConnections';
import { cn } from '@/lib/utils';
import {
  type Incident,
  type IncidentSummary,
  type IncidentFilters,
  type IncidentStatus,
  type IncidentSeverity,
  type ConversationGroup,
  DEFAULT_INCIDENT_FILTERS,
  SEVERITY_CONFIG,
  applyIncidentFilters,
  groupBySeverity,
  groupByConversation,
  isTerminalStatus,
} from '@/lib/incidentUtils';

function DashboardHeader({ summary }: { summary: IncidentSummary }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <ShieldAlert className="h-6 w-6" />
        Incident Dashboard
      </h1>
      <div className="flex gap-2 mt-2 flex-wrap">
        {summary.bySeverity.critical > 0 && (
          <Badge className="bg-red-600 text-white">{summary.bySeverity.critical} Critical</Badge>
        )}
        {summary.bySeverity.high > 0 && (
          <Badge className="bg-red-500 text-white">{summary.bySeverity.high} High</Badge>
        )}
        {summary.bySeverity.medium > 0 && (
          <Badge className="bg-yellow-500 text-black">{summary.bySeverity.medium} Medium</Badge>
        )}
        {summary.bySeverity.low > 0 && (
          <Badge className="bg-blue-500 text-white">{summary.bySeverity.low} Low</Badge>
        )}
        {summary.total === 0 && (
          <span className="text-muted-foreground text-sm">No active incidents</span>
        )}
      </div>
    </div>
  );
}

function IncidentFilterBar({
  filters,
  onChange,
  connectionOptions,
}: {
  filters: IncidentFilters;
  onChange: (f: IncidentFilters) => void;
  connectionOptions: { id: string; name: string }[];
}) {
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Select
        value={filters.status}
        onValueChange={(v) => onChange({ ...filters, status: v as IncidentFilters['status'] })}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Statuses</SelectItem>
          <SelectItem value="PENDING">Pending</SelectItem>
          <SelectItem value="ACTIVE">Active</SelectItem>
          <SelectItem value="ESCALATED">Escalated</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.severity}
        onValueChange={(v) => onChange({ ...filters, severity: v as IncidentFilters['severity'] })}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Severity" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Severities</SelectItem>
          <SelectItem value="critical">Critical</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>

      {connectionOptions.length > 1 && (
        <Select
          value={filters.connectionId}
          onValueChange={(v) => onChange({ ...filters, connectionId: v })}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Connection" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Connections</SelectItem>
            {connectionOptions.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search incidents..."
          value={filters.searchQuery}
          onChange={(e) => onChange({ ...filters, searchQuery: e.target.value })}
          className="pl-8"
        />
      </div>
    </div>
  );
}

function IncidentDashboardSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

type GroupingMode = 'severity' | 'conversation';

function IncidentListView({
  incidents,
  loading,
  error,
  connectionNames,
  connectionOptions,
}: {
  incidents: Incident[];
  loading: boolean;
  error: string | null;
  connectionNames: Record<string, string>;
  connectionOptions: { id: string; name: string }[];
}) {
  const [filters, setFilters] = useState<IncidentFilters>(DEFAULT_INCIDENT_FILTERS);
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('severity');
  const [resolvedOpen, setResolvedOpen] = useState(false);

  // Fetch all incidents (including closed) for the resolved section
  const { incidents: allIncidents, loading: allLoading } = useAllIncidents({ includeClosedIncidents: true });
  const closedIncidents = allIncidents.filter(i => isTerminalStatus(i.status));

  const filtered = applyIncidentFilters(incidents, filters);
  const severityGroups = groupingMode === 'severity' ? groupBySeverity(filtered) : [];
  const conversationGroups = groupingMode === 'conversation' ? groupByConversation(filtered) : [];

  const hasResults = groupingMode === 'severity' ? severityGroups.length > 0 : conversationGroups.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 items-center">
        <IncidentFilterBar filters={filters} onChange={setFilters} connectionOptions={connectionOptions} />
      </div>

      {/* ADC-006B F-09: Grouping toggle */}
      <div className="flex gap-1">
        <Button
          variant={groupingMode === 'severity' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGroupingMode('severity')}
        >
          By Severity
        </Button>
        <Button
          variant={groupingMode === 'conversation' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGroupingMode('conversation')}
        >
          By Conversation
        </Button>
      </div>

      {loading && <IncidentDashboardSkeleton />}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loading && !error && !hasResults && (
        <Card className="p-8 text-center">
          <CardContent className="pt-0">
            <ShieldCheck className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All Clear</h3>
            <p className="text-muted-foreground">No active incidents across any conversations.</p>
          </CardContent>
        </Card>
      )}

      {groupingMode === 'severity' && severityGroups.map(group => (
        <div key={group.severity}>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
            <span className={cn("h-3 w-3 rounded-full", SEVERITY_CONFIG[group.severity].dotColor)} />
            {SEVERITY_CONFIG[group.severity].label} ({group.count})
          </h2>
          <div className="space-y-2">
            {group.incidents.map(incident => (
              <IncidentCard key={incident.id} incident={incident} connectionName={connectionNames[incident.connection_id]} />
            ))}
          </div>
        </div>
      ))}

      {groupingMode === 'conversation' && conversationGroups.map(group => (
        <div key={group.conversation_id}>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
            <span className={cn("h-3 w-3 rounded-full", SEVERITY_CONFIG[group.highest_severity].dotColor)} />
            {group.conversation_name}
            {connectionNames[group.connection_id] && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal">
                {connectionNames[group.connection_id]}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {group.incident_count} incident{group.incident_count !== 1 ? 's' : ''}
            </Badge>
          </h2>
          <div className="space-y-2">
            {group.incidents.map(incident => (
              <IncidentCard key={incident.id} incident={incident} connectionName={connectionNames[incident.connection_id]} />
            ))}
          </div>
        </div>
      ))}

      {/* Resolved incidents — collapsed by default */}
      {!loading && closedIncidents.length > 0 && (
        <>
          <Separator />
          <Collapsible open={resolvedOpen} onOpenChange={setResolvedOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between px-2 py-2 h-auto">
                <span className="flex items-center gap-2 text-muted-foreground">
                  {resolvedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <ShieldCheck className="h-4 w-4" />
                  Resolved Incidents
                </span>
                <Badge variant="secondary" className="text-xs">
                  {closedIncidents.length}
                </Badge>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 mt-2">
              {closedIncidents.map(incident => (
                <div key={incident.id} className="opacity-70">
                  <IncidentCard incident={incident} connectionName={connectionNames[incident.connection_id]} />
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
}

function IncidentDetailView({
  connectionId,
  conversationId,
  incidentId,
  incidents,
  actions,
}: {
  connectionId: string;
  conversationId: string;
  incidentId: string;
  incidents: Incident[];
  actions: {
    resolveIncident: (incident: Incident, notes: string) => Promise<void>;
    dismissIncident: (incident: Incident, notes: string) => Promise<void>;
    escalateIncident: (incident: Incident, escalateTo: string) => Promise<void>;
  };
}) {
  const navigate = useNavigate();
  const incident = incidents.find(
    i => i.id === incidentId && i.connection_id === connectionId && i.conversation_id === conversationId
  );

  if (!incident) {
    return (
      <Card className="p-8 text-center">
        <CardContent className="pt-0">
          <h3 className="text-lg font-medium">Incident not found</h3>
          <p className="text-muted-foreground mb-4">
            This incident may have been resolved or does not exist.
          </p>
          <Button variant="outline" onClick={() => navigate('/incidents')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <IncidentDetailPanel
      incident={incident}
      onClose={() => navigate('/incidents')}
      onResolve={(notes) => actions.resolveIncident(incident, notes)}
      onDismiss={(notes) => actions.dismissIncident(incident, notes)}
      onEscalate={(target) => actions.escalateIncident(incident, target)}
    />
  );
}

export default function IncidentDashboard() {
  const { connectionId, conversationId, incidentId } = useParams<{
    connectionId?: string;
    conversationId?: string;
    incidentId?: string;
  }>();
  const navigate = useNavigate();
  const { incidents, summary, loading, error, resolveIncident, dismissIncident, escalateIncident } = useIncidentContext();
  const { incidents: allIncidents } = useAllIncidents({ includeClosedIncidents: true });
  const { connections } = useConnections();

  // Build connection name lookup and options for filter dropdown
  const connectionNames: Record<string, string> = {};
  const connectionOptions: { id: string; name: string }[] = [];
  for (const c of connections) {
    connectionNames[c.id] = c.name;
    connectionOptions.push({ id: c.id, name: c.name });
  }

  const isDetailView = !!(connectionId && conversationId && incidentId);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background w-full">
        <Navbar />
        <div className="flex min-h-[calc(100vh-4rem)] pt-16 w-full">
          <AppSidebar />
          <main className="flex-1 overflow-auto px-4 md:px-6 pb-6 pt-4">
            <div className="max-w-6xl mx-auto">
              {isDetailView && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mb-4"
                  onClick={() => navigate('/incidents')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              )}
              <DashboardHeader summary={summary} />
              {isDetailView ? (
                <IncidentDetailView
                  connectionId={connectionId!}
                  conversationId={conversationId!}
                  incidentId={incidentId!}
                  incidents={allIncidents}
                  actions={{ resolveIncident, dismissIncident, escalateIncident }}
                />
              ) : (
                <IncidentListView
                  incidents={incidents}
                  loading={loading}
                  error={error}
                  connectionNames={connectionNames}
                  connectionOptions={connectionOptions}
                />
              )}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
